// Growlab 811 — page chrome: header HUD, equipment index, inspector card, motion controls.
import React, { useEffect, useState } from "react";
import { DEVICES, TREES } from "./data";

export function Header({ tour, onTour }) {
  const [m, setM] = useState({ vpd: 1.05, temp: 82.4, rh: 76 });
  useEffect(() => {
    const id = setInterval(() => setM({
      vpd: +(1.05 + (Math.random() - 0.5) * 0.08).toFixed(2),
      temp: +(82.4 + (Math.random() - 0.5) * 1.2).toFixed(1),
      rh: Math.round(76 + (Math.random() - 0.5) * 5),
    }), 2400);
    return () => clearInterval(id);
  }, []);
  return (
    <header className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] bg-stone-950/90 backdrop-blur-xl border-b border-amber-500/20" data-testid="growlab-header">
      <div className="min-w-0">
        <h1 className="text-base sm:text-xl font-extrabold tracking-tight text-stone-100 truncate" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
          CLOUDLAB 811 <span className="text-amber-400">//</span> KINETIC BUILD MODEL
        </h1>
        <p className="text-[10px] sm:text-xs font-mono tracking-widest text-stone-400 uppercase">Tropical ecosystem directive — equipment in motion</p>
      </div>
      <div className="flex items-center gap-2 sm:gap-3">
        {[["VPD", `${m.vpd} kPa`, "hud-vpd"], ["TEMP", `${m.temp}°F`, "hud-temp"], ["RH", `${m.rh}%`, "hud-rh"]].map(([k, v, tid]) => (
          <div key={k} className="hidden sm:block px-3 py-1.5 rounded-lg bg-slate-900/70 border border-slate-700/50" data-testid={tid}>
            <div className="text-[9px] font-mono tracking-widest text-amber-400/90">{k}</div>
            <div className="text-sm font-mono text-stone-100">{v}</div>
          </div>
        ))}
        <button
          data-testid="tour-toggle-button"
          onClick={onTour}
          className={`px-3 sm:px-4 py-2 rounded-lg font-mono text-[11px] tracking-widest border transition-colors duration-200 min-h-[44px] ${
            tour ? "bg-amber-400 text-stone-950 border-amber-300" : "bg-stone-950 text-amber-300 border-amber-500/40 hover:border-amber-400"
          }`}
        >
          {tour ? "STOP TOUR" : "AUTO-TOUR"}
        </button>
      </div>
    </header>
  );
}

export function EquipmentIndex({ selected, onSelect }) {
  return (
    <aside className="hidden md:flex flex-col gap-2 absolute left-4 top-4 bottom-4 w-64 p-3 rounded-xl bg-stone-950/85 backdrop-blur-xl border border-amber-500/20 shadow-2xl z-10 overflow-y-auto" data-testid="equipment-index">
      <div className="text-[10px] font-mono tracking-widest text-amber-400/90 uppercase px-1 pb-1 border-b border-slate-700/50">Equipment index · UIS map</div>
      {DEVICES.map((d) => (
        <button
          key={d.id}
          data-testid={`device-item-${d.id}`}
          onClick={() => onSelect(d.id)}
          className={`text-left px-3 py-2 rounded-lg border transition-all duration-200 min-h-[44px] ${
            selected === d.id ? "bg-amber-400/10 border-amber-400/70" : "bg-slate-900/50 border-slate-700/50 hover:border-amber-500/40"
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color, boxShadow: `0 0 6px ${d.color}` }} />
            <span className="text-xs font-semibold text-stone-100">{d.name}</span>
          </div>
          <div className="text-[9px] font-mono tracking-widest text-stone-400 mt-0.5">{d.port}</div>
        </button>
      ))}
    </aside>
  );
}

export function InspectorCard({ selected, onClose }) {
  const d = DEVICES.find((x) => x.id === selected);
  if (!d) {
    return (
      <aside className="hidden md:block absolute right-4 top-4 w-80 p-4 rounded-xl bg-stone-950/85 backdrop-blur-xl border border-slate-700/50 z-10" data-testid="device-inspector-empty">
        <div className="relative h-36 rounded-lg overflow-hidden border border-amber-500/20 mb-3">
          <img src="/assets/growlab/hero.png" alt="Photoreal render of the finished tropical tent ecosystem" className="w-full h-full object-cover growlab-kenburns" />
          <span className="absolute bottom-1.5 left-2 text-[8px] font-mono tracking-widest text-amber-300/80">TARGET BUILD · PHOTOREAL AI RENDER</span>
        </div>
        <p className="text-xs font-mono tracking-widest text-stone-400 leading-relaxed">SELECT A DEVICE IN THE SCENE — OR RUN THE AUTO-TOUR — TO READ ITS ROLE IN THE BUILD.</p>
        <div className="grid grid-cols-5 gap-1.5 mt-3">
          {TREES.map((t) => (
            <figure key={t.name} className="text-center" data-testid={`tree-thumb-${t.name.toLowerCase()}`}>
              <img src={t.img} alt={`${t.name} tree`} className="w-full h-12 object-cover rounded border border-emerald-500/20" />
              <figcaption className="text-[8px] font-mono tracking-widest text-emerald-300/80 mt-0.5">{t.name}</figcaption>
            </figure>
          ))}
        </div>
      </aside>
    );
  }
  return (
    <aside className="absolute z-20 left-2 right-2 bottom-2 top-auto max-h-[62%] overflow-y-auto sm:left-auto sm:right-4 sm:top-4 sm:bottom-auto sm:w-80 sm:max-h-none p-4 rounded-xl bg-stone-950/90 backdrop-blur-xl border border-amber-500/30 shadow-2xl" data-testid="device-inspector">
      <div className="relative h-28 sm:h-44 rounded-lg overflow-hidden border border-amber-500/25 mb-3" data-testid="inspector-photo">
        <video
          key={d.id}
          src={`/assets/growlab/video/${d.id}.mp4`}
          poster={d.uhd || d.img}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "block"; }}
        />
        <img src={d.img} alt={`Photoreal render of ${d.name}`} className="w-full h-full object-cover growlab-kenburns" style={{ display: "none" }} />
        <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-stone-950/90 to-transparent" />
        <span className="absolute bottom-1.5 left-2 text-[8px] font-mono tracking-widest text-amber-300/80">MOTION RENDER · AI</span>
      </div>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[9px] font-mono tracking-widest" style={{ color: d.color }}>{d.tag}</div>
          <h2 className="text-lg font-bold text-stone-100" style={{ fontFamily: "Georgia, serif" }}>{d.name}</h2>
        </div>
        <button data-testid="inspector-close-button" onClick={onClose} className="text-stone-400 hover:text-amber-300 font-mono text-sm px-2 min-h-[44px]">✕</button>
      </div>
      <div className="text-[10px] font-mono tracking-widest text-amber-400/90 mt-1">{d.port}</div>
      <p className="text-sm text-stone-300 leading-relaxed mt-3">{d.role}</p>
      <div className="grid grid-cols-3 gap-2 mt-4">
        {d.metrics.map(([k, v]) => (
          <div key={k} className="px-2 py-1.5 rounded bg-slate-900/70 border border-slate-700/50">
            <div className="text-[8px] font-mono tracking-widest text-stone-500">{k}</div>
            <div className="text-[11px] font-mono text-stone-100">{v}</div>
          </div>
        ))}
      </div>
    </aside>
  );
}

export function TourSlide({ deviceId, index, total, onStop }) {
  const d = DEVICES.find((x) => x.id === deviceId);
  if (!d) return null;
  return (
    <div className="absolute inset-0 z-20 bg-stone-950" data-testid="tour-slide">
      <video
        key={d.id}
        src={`/assets/growlab/video/${d.id}.mp4`}
        poster={d.uhd || d.img}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="tour-slide-video"
        onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "block"; }}
      />
      <img
        key={`img-${d.id}`}
        src={d.uhd || d.img}
        alt={`Ultra-HD photoreal render of ${d.name}`}
        onError={(e) => { if (!e.currentTarget.dataset.fell) { e.currentTarget.dataset.fell = "1"; e.currentTarget.src = d.img; } }}
        className="absolute inset-0 w-full h-full object-cover growlab-kenburns"
        data-testid="tour-slide-image"
        style={{ display: "none" }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/20 to-stone-950/40" />
      <span className="absolute top-4 left-4 px-2.5 py-1.5 rounded bg-stone-950/85 border border-amber-500/30 text-[10px] font-mono tracking-widest text-amber-300">
        {index + 1} / {total} · {d.port} · ULTRA-HD AI RENDER
      </span>
      <button data-testid="tour-slide-close" onClick={onStop} className="absolute top-4 right-4 px-3 min-h-[44px] rounded bg-stone-950/85 border border-slate-600/60 text-stone-300 hover:text-amber-300 font-mono text-[10px] tracking-widest">
        EXIT TOUR
      </button>
      <figure className="absolute inset-x-0 bottom-0 p-5 sm:p-8 max-w-4xl">
        <div className="text-[10px] font-mono tracking-widest" style={{ color: d.color }}>{d.tag}</div>
        <h2 className="text-2xl sm:text-4xl font-bold text-stone-100 drop-shadow-lg" style={{ fontFamily: "Georgia, serif" }}>{d.name}</h2>
        <p className="text-sm sm:text-base text-stone-300 leading-relaxed mt-2 max-w-2xl drop-shadow">{d.role}</p>
        <div className="flex gap-2 mt-3 flex-wrap">
          {d.metrics.map(([k, v]) => (
            <span key={k} className="px-2.5 py-1 rounded bg-stone-950/80 border border-slate-700/60 text-[10px] font-mono text-stone-200">{k}: {v}</span>
          ))}
        </div>
      </figure>
    </div>
  );
}

export function BottomBar({ running, onRun, speed, onSpeed, dense, onDense, walls, onWalls, etfe, onEtfe, realistic, onRealistic, onReset, onRetrofit }) {
  const btn = "px-3 py-2 rounded-lg font-mono text-[10px] tracking-widest border transition-colors duration-200 min-h-[44px]";
  const on = "bg-amber-400 text-stone-950 border-amber-300";
  const off = "bg-stone-950 text-stone-300 border-slate-700/60 hover:border-amber-500/50";
  return (
    <footer className="flex items-center gap-2 px-4 sm:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-stone-950/90 backdrop-blur-xl border-t border-amber-500/20 overflow-x-auto" data-testid="growlab-bottom-bar">
      <button data-testid="kinetic-play-toggle" onClick={onRun} className={`${btn} ${running ? on : off}`}>{running ? "❚❚ PAUSE" : "▶ RUN"}</button>
      <button data-testid="speed-toggle" onClick={onSpeed} className={`${btn} ${off}`}>{speed}× SPEED</button>
      <button data-testid="particle-toggle" onClick={onDense} className={`${btn} ${dense ? on : off}`}>PARTICLES {dense ? "HI" : "LO"}</button>
      <button data-testid="walls-toggle" onClick={onWalls} className={`${btn} ${walls ? on : off}`}>CANVAS {walls ? "ON" : "OFF"}</button>
      <button data-testid="etfe-shell-toggle" onClick={onEtfe} className={`${btn} ${etfe ? "bg-emerald-400 text-stone-950 border-emerald-300" : "bg-stone-950 text-emerald-300 border-emerald-500/40 hover:border-emerald-400"}`}>ETFE SHELL {etfe ? "ON" : "OFF"}</button>
      <button data-testid="realistic-toggle" onClick={onRealistic} className={`${btn} ${realistic ? "bg-sky-400 text-stone-950 border-sky-300" : "bg-stone-950 text-sky-300 border-sky-500/40 hover:border-sky-400"}`}>{realistic ? "PHOTOREAL" : "SCHEMATIC"}</button>
      <button data-testid="camera-reset-button" onClick={onReset} className={`${btn} ${off}`}>RESET CAM</button>
      <button data-testid="retrofit-open-button" onClick={onRetrofit} className={`${btn} bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:border-emerald-400`}>ETFE RETROFIT</button>
      <span className="ml-auto hidden lg:block text-[9px] font-mono tracking-widest text-stone-500 whitespace-nowrap">
        DRAG TO ORBIT · SCROLL TO ZOOM · TAP A LABEL TO INSPECT
      </span>
    </footer>
  );
}
